'use client'

import * as React from 'react'
import { format as formatDate } from 'date-fns'
import { dateFnsLocale } from '@/lib/date-display'

type ViewMode = 'days' | 'months' | 'years'

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}
function endOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0)
}
function addMonths(d: Date, n: number) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1)
}
function fmtISO(d: Date) {
  const y = d.getFullYear()
  const m = `${d.getMonth() + 1}`.padStart(2, '0')
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function Calendar({
  month,
  value,
  rangeStart,
  rangeEnd,
  onChange,
  disabledDates,
  hideNavigation = false,
  hideHeader = false,
  minDate,
  maxDate,
  fullWidth = false,
  locale,
}: {
  month?: Date
  value?: string
  rangeStart?: string
  rangeEnd?: string
  onChange?: (next: string) => void
  disabledDates?: Set<string>
  hideNavigation?: boolean
  hideHeader?: boolean
  minDate?: string
  maxDate?: string
  fullWidth?: boolean
  /** BCP 47 locale tag (e.g. 'ar', 'en'). Falls back to browser default. */
  locale?: string
}) {
  const [view, setView] = React.useState<Date>(() => startOfMonth(month ?? new Date()))
  const [mode, setMode] = React.useState<ViewMode>('days')
  const [yearRangeStart, setYearRangeStart] = React.useState(() => Math.floor(view.getFullYear() / 12) * 12)

  React.useEffect(() => {
    if (month) setView(startOfMonth(month))
  }, [month])

  const first = startOfMonth(view)
  const last = endOfMonth(view)
  const startWeekday = first.getDay()
  const total = last.getDate()
  const days: (Date | null)[] = []
  for (let i = 0; i < startWeekday; i++) days.push(null)
  for (let d = 1; d <= total; d++) days.push(new Date(view.getFullYear(), view.getMonth(), d))

  const selected = value || ''
  const rs = rangeStart || ''
  const re = rangeEnd || ''
  const isBetween = (iso: string) => {
    if (!rs || !re) return false
    const a = new Date(rs).getTime()
    const b = new Date(re).getTime()
    const x = new Date(iso).getTime()
    const [min, max] = a <= b ? [a, b] : [b, a]
    return x > min && x < max
  }

  // date-fns (bundled locale data) rather than Intl: Intl output for 'ar' differs
  // between WebKit and V8/Node, so any label present in SSR HTML would hydration-
  // mismatch on Safari. date-fns is byte-identical on server and every browser.
  const fnsLocale = dateFnsLocale(locale)
  const weekdays = [...Array(7)].map((_, i) =>
    formatDate(new Date(2021, 0, i + 3), 'EEE', { locale: fnsLocale })
  )

  // Generate years for year picker (12 years grid)
  const years = Array.from({ length: 12 }, (_, i) => yearRangeStart + i)

  const handleMonthSelect = (monthIndex: number) => {
    setView(new Date(view.getFullYear(), monthIndex, 1))
    setMode('days')
  }

  const handleYearSelect = (year: number) => {
    setView(new Date(year, view.getMonth(), 1))
    setMode('months')
  }

  const handleHeaderClick = () => {
    if (mode === 'days') {
      setMode('months')
    } else if (mode === 'months') {
      setYearRangeStart(Math.floor(view.getFullYear() / 12) * 12)
      setMode('years')
    }
  }

  const handlePrevNav = () => {
    if (mode === 'days') {
      setView(v => addMonths(v, -1))
    } else if (mode === 'months') {
      setView(v => new Date(v.getFullYear() - 1, v.getMonth(), 1))
    } else {
      setYearRangeStart(y => y - 12)
    }
  }

  const handleNextNav = () => {
    if (mode === 'days') {
      setView(v => addMonths(v, 1))
    } else if (mode === 'months') {
      setView(v => new Date(v.getFullYear() + 1, v.getMonth(), 1))
    } else {
      setYearRangeStart(y => y + 12)
    }
  }

  const getHeaderLabel = () => {
    if (mode === 'days') {
      return formatDate(view, 'LLLL yyyy', { locale: fnsLocale })
    } else if (mode === 'months') {
      return view.getFullYear().toString()
    } else {
      return `${yearRangeStart} - ${yearRangeStart + 11}`
    }
  }

  // Check if a year is within valid range
  const isYearDisabled = (year: number) => {
    if (minDate) {
      const minYear = new Date(minDate).getFullYear()
      if (year < minYear) return true
    }
    if (maxDate) {
      const maxYear = new Date(maxDate).getFullYear()
      if (year > maxYear) return true
    }
    return false
  }

  // Check if a month is within valid range
  const isMonthDisabled = (monthIndex: number) => {
    const year = view.getFullYear()
    if (minDate) {
      const min = new Date(minDate)
      if (year < min.getFullYear() || (year === min.getFullYear() && monthIndex < min.getMonth())) {
        return true
      }
    }
    if (maxDate) {
      const max = new Date(maxDate)
      if (year > max.getFullYear() || (year === max.getFullYear() && monthIndex > max.getMonth())) {
        return true
      }
    }
    return false
  }

  return (
    <div className={fullWidth ? 'w-full text-sm select-none' : 'w-[260px] text-sm select-none'}>
      {!hideHeader && (
        <div className={`flex items-center px-2 py-1.5 border-b ${hideNavigation ? 'justify-center' : 'justify-between'}`}>
          {!hideNavigation && (
            <button
              type="button"
              className="h-7 w-7 rounded-md border hover:bg-muted flex items-center justify-center"
              onClick={handlePrevNav}
              aria-label="Previous"
            >
              ‹
            </button>
          )}
          <button
            type="button"
            onClick={handleHeaderClick}
            className="font-medium hover:bg-muted px-2 py-0.5 rounded transition-colors"
          >
            {getHeaderLabel()}
          </button>
          {!hideNavigation && (
            <button
              type="button"
              className="h-7 w-7 rounded-md border hover:bg-muted flex items-center justify-center"
              onClick={handleNextNav}
              aria-label="Next"
            >
              ›
            </button>
          )}
        </div>
      )}

      {/* Days View */}
      {mode === 'days' && (
        <>
          <div className={`grid grid-cols-7 p-2 text-muted-foreground ${fullWidth ? 'gap-0 text-sm font-medium' : 'gap-1 text-[11px]'}`}>
            {weekdays.map(w => (
              <div key={w} className="text-center py-1">{w}</div>
            ))}
          </div>
          <div className={`grid grid-cols-7 p-2 ${fullWidth ? 'gap-0' : 'gap-1'}`}>
            {days.map((d, idx) => {
              if (!d) return <div key={`e-${idx}`} className={fullWidth ? 'aspect-square' : ''} />
              const iso = fmtISO(d)
              const isSel = iso === selected || iso === rs || iso === re
              const inRange = !isSel && isBetween(iso)
              const isBeforeMin = minDate ? iso < minDate : false
              const isAfterMax = maxDate ? iso > maxDate : false
              const isDisabled = isBeforeMin || isAfterMax || (disabledDates?.has(iso) ?? false)
              return (
                <button
                  key={iso}
                  type="button"
                  onClick={() => {
                    if (isDisabled) return
                    onChange?.(iso)
                  }}
                  disabled={isDisabled}
                  className={
                    (fullWidth
                      ? 'aspect-square w-full rounded-lg text-center text-base font-medium '
                      : 'h-8 w-8 rounded-md text-center ') +
                    (isDisabled
                      ? 'cursor-not-allowed bg-muted/50 text-muted-foreground'
                      : isSel
                      ? 'bg-primary text-primary-foreground'
                      : inRange
                      ? 'bg-primary/10'
                      : 'hover:bg-muted active:bg-muted')
                  }
                >
                  {d.getDate()}
                </button>
              )
            })}
          </div>
        </>
      )}

      {/* Months View */}
      {mode === 'months' && (
        <div className="grid grid-cols-3 gap-2 p-3">
          {Array.from({ length: 12 }, (_, idx) => {
            const label = formatDate(new Date(2024, idx, 1), 'LLL', { locale: fnsLocale })
            const isCurrentMonth = view.getMonth() === idx
            const isDisabled = isMonthDisabled(idx)
            return (
              <button
                key={idx}
                type="button"
                onClick={() => !isDisabled && handleMonthSelect(idx)}
                disabled={isDisabled}
                className={`
                  py-2 px-1 rounded-md text-sm font-medium transition-colors
                  ${isDisabled
                    ? 'cursor-not-allowed text-muted-foreground/50'
                    : isCurrentMonth
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                  }
                `}
              >
                {label}
              </button>
            )
          })}
        </div>
      )}

      {/* Years View */}
      {mode === 'years' && (
        <div className="grid grid-cols-3 gap-2 p-3">
          {years.map(year => {
            const isCurrentYear = view.getFullYear() === year
            const isDisabled = isYearDisabled(year)
            return (
              <button
                key={year}
                type="button"
                onClick={() => !isDisabled && handleYearSelect(year)}
                disabled={isDisabled}
                className={`
                  py-2 px-1 rounded-md text-sm font-medium transition-colors
                  ${isDisabled
                    ? 'cursor-not-allowed text-muted-foreground/50'
                    : isCurrentYear
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                  }
                `}
              >
                {year}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
